import { Timeline } from "@mantine/core";
import {
    IconCalendarBolt,
    IconCircleCheck,
    IconClipboardCheck,
    IconHome,
    IconRefreshDot,
    IconTruckDelivery,
    IconTruckLoading,
} from "@tabler/icons-react";
import moment from "moment";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { EOrderStatus, IOrder } from "../interfaces/IOrder";
import CardWrapper from "./general/CardWrapper";

interface IStatusCardProps {
    status: EOrderStatus;
    isDelivery?: boolean; // true = delivery, false = in-house, undefined = show all
    order?: IOrder;
}

enum EDeliveryOrInHouse {
    DELIVERY = "DELIVERY",
    IN_HOUSE = "IN_HOUSE",
    BOTH = "BOTH",
}

type StatusItem = {
    label: string;
    value: EOrderStatus;
    icon: React.ReactNode;
    deliveryOrInHouse: EDeliveryOrInHouse;
    description?: string;
};

export const formatDateTime = (
    date: Date | undefined,
    withHours = false,
    format = "ddd, DD.MM.YYYY",
) => {
    if (!date) {
        return "-";
    }

    const m = moment(date);
    if (!m.isValid()) {
        return "-";
    }

    if (withHours) {
        return m.format(`${format}, HH:mm`);
    }

    return m.format(format);
};

const formatHours = (
    startDate: string | undefined,
    endDate: string | undefined,
) => {
    if (!startDate || !endDate) {
        return "-";
    }

    const start = moment(startDate);
    const end = moment(endDate);

    if (!start.isValid() || !end.isValid()) {
        return "-";
    }

    return `${start.format("HH")} - ${end.format("HH")}`;
};
const getStatuses = (t: any, order?: IOrder): StatusItem[] => {
    const pickup = formatDateTime(order?.pickupDate, true);
    const pickupProgrammedDate = formatDateTime(
        order?.pickUpDateAproximate,
        false,
    );
    const pickupProgrammedHours = formatHours(
        order?.aproximateHourPickup?.start,

        order?.aproximateHourPickup?.end,
    );
    // For delivery, you might have either exact deliveryDate or "aproximateDelivery + aproximateHourDelivery"
    const deliveryDate = formatDateTime(order?.deliveryDate, true);

    const approxDeliveryDate = order?.aproximateDelivery
        ? formatDateTime(order?.aproximateDelivery)
        : "";

    const approxDeliveryHours = order?.aproximateHourDelivery || "";

    return [
        {
            label: t(
                "v2.OrderPage.orderDetails.statusCard.statuses.pickedUpInProgress",
            ),
            value: EOrderStatus.PICKED_UP_IN_PROGRESS,
            icon: <IconCalendarBolt size={18} />,
            deliveryOrInHouse: EDeliveryOrInHouse.DELIVERY,
            description: order?.pickUpDateAproximate
                ? t(
                    "v2.OrderPage.orderDetails.statusCard.statusesDescription.pickedUpInProgress",
                    {
                        date: pickupProgrammedDate,
                        time: pickupProgrammedHours,
                    },
                )
                : "",
        },
        {
            label: t("v2.OrderPage.orderDetails.statusCard.statuses.collected"),
            value: EOrderStatus.PICKED_UP,
            icon: <IconTruckLoading size={18} />,
            deliveryOrInHouse: EDeliveryOrInHouse.BOTH,
            description: order?.pickupDate
                ? t(
                    "v2.OrderPage.orderDetails.statusCard.statusesDescription.collected",
                    {
                        date: pickup,
                    },
                )
                : "",
        },
        {
            label: t("v2.OrderPage.orderDetails.statusCard.statuses.measuring"),
            value: EOrderStatus.MEASURED,
            icon: <IconRefreshDot size={18} />,
            deliveryOrInHouse: EDeliveryOrInHouse.BOTH,
        },
        {
            label: t("v2.OrderPage.orderDetails.statusCard.statuses.washing"),
            value: EOrderStatus.WASHED,
            icon: <IconClipboardCheck size={18} />,
            deliveryOrInHouse: EDeliveryOrInHouse.BOTH,
        },
        {
            label: t("v2.OrderPage.orderDetails.statusCard.statuses.delivering"),
            value: EOrderStatus.FOR_DELIVERY,
            icon: <IconTruckDelivery size={18} />,
            deliveryOrInHouse: EDeliveryOrInHouse.DELIVERY,
            description:
                order?.deliveryDate ||
                    order?.aproximateDelivery ||
                    (order as any)?.aproximateHourDelivery
                    ? t(
                        "v2.OrderPage.orderDetails.statusCard.statusesDescription.delivering",
                        {
                            date: approxDeliveryDate,
                            time: approxDeliveryHours,
                        },
                    )
                    : "",
        },
        {
            label: t("v2.OrderPage.orderDetails.statusCard.statuses.ready"),
            value: EOrderStatus.FOR_DELIVERY_IN_HOUSE,
            icon: <IconHome size={18} />,
            deliveryOrInHouse: EDeliveryOrInHouse.IN_HOUSE,
            description: order?.aproximateDelivery
                ? order?.status === EOrderStatus.FOR_DELIVERY_IN_HOUSE
                    ? t(
                        "v2.OrderPage.orderDetails.statusCard.statusesDescription.ready",
                        {
                            date: approxDeliveryDate,
                        },
                    )
                    : t(
                        "v2.OrderPage.orderDetails.statusCard.statusesDescription.readyProgrammed",
                        {
                            date: approxDeliveryDate,
                        },
                    )
                : undefined,
        },
        {
            label: t("v2.OrderPage.orderDetails.statusCard.statuses.delivered"),
            value: EOrderStatus.DELIVERED,
            icon: <IconCircleCheck size={18} />,
            deliveryOrInHouse: EDeliveryOrInHouse.BOTH,
            description: order?.deliveryDate
                ? t(
                    "v2.OrderPage.orderDetails.statusCard.statusesDescription.delivered",
                    {
                        date: deliveryDate,
                    },
                )
                : "",
        },
    ];
};

export default function StatusCardTimelineV5({
    status,
    isDelivery,
    order,
}: IStatusCardProps) {
    const { t } = useTranslation();

    const items = useMemo(() => getStatuses(t, order), [t, order]);

    // 1) Filter steps by order type (delivery / in-house)
    const filtered = useMemo(() => {
        return items.filter((item) => {
            if (isDelivery === true) {
                return (
                    item.deliveryOrInHouse === EDeliveryOrInHouse.DELIVERY ||
                    item.deliveryOrInHouse === EDeliveryOrInHouse.BOTH
                );
            }
            if (isDelivery === false) {
                return (
                    item.deliveryOrInHouse === EDeliveryOrInHouse.IN_HOUSE ||
                    item.deliveryOrInHouse === EDeliveryOrInHouse.BOTH
                );
            }
            return true;
        });
    }, [items, isDelivery]);

    // 2) Compute active index safely
    const rawIndex = filtered.findIndex((x) => x.value === status);
    const activeIndex = rawIndex === -1 ? 0 : rawIndex;

    return (
        <CardWrapper style={{ height: "100%", display: "flex" }}>
            <Timeline
                active={activeIndex}
                lineWidth={3}
                bulletSize={28}
                color="yellow"
                styles={{
                    item: { color: "var(--text-secondary)" },
                    itemTitle: { fontWeight: 600 },
                    itemBody: { color: "var(--text-secondary)" },
                }}
            >
                {filtered.map((item, idx) => (
                    <Timeline.Item
                        key={item.value}
                        title={item.label}
                        bullet={item.icon}
                        lineVariant={idx >= activeIndex ? "dashed" : "solid"}
                    >
                        {item.description ? (
                            <div style={{ fontSize: 13, lineHeight: 1.35 }}>
                                {item.description}
                            </div>
                        ) : null}
                    </Timeline.Item>
                ))}
            </Timeline>
        </CardWrapper>
    );
}
